<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useUserStore } from '@/stores/user'
  import { useModalStore } from '@/stores/modal'
  import { useCreatorStore } from '@/stores/creator'
  import { useLocalImportStore } from '@/stores/local-import'
  import { useRemoteImportStore } from '@/stores/remote-import'
  import { useDropZone } from '@vueuse/core'
  import { track } from '@/helpers/mixpanelDes'
  import { getDroppedImportUrl } from '@/helpers/url'
  import DesLayout from '@/views/DesLayout.vue'
  import DesHomeHero from '@/components/home/DesHomeHero.vue'
  import DesHomeChatWidget from '@/components/home/DesHomeChatWidget.vue'
  import DesHomeRecentProjects from '@/components/home/DesHomeRecentProjects.vue'
  import DesSearchWithResults from '@/components/DesSearchWithResults.vue'
  import DesEnter from '@/components/DesEnter.vue'
  import DesTile from '@/components/home/DesTile.vue'
  import DesTileDnD from '@/components/home/DesTileDnD.vue'

  const user = useUserStore()
  const creator = useCreatorStore()
  const localImport = useLocalImportStore()
  const remoteImport = useRemoteImportStore()
  const modal = useModalStore()
  const canUsePrintBooks = user.accountType?.printbook > 0
  const canUseAudiobooks = user.accountType?.tts_credits > 0
  const droppedFiles = ref<FileList | null>(null)
  const droppedUrl = ref<string | null>(null)

  // Dropping a file or URL anywhere on the home page starts the matching import
  // flow, like the old visible drop tile did. The zone is on <body> so drops on
  // teleported popups are covered too; useDropZone removes its listeners when
  // this view unmounts, so other pages are unaffected.
  useDropZone(document.body, (files, event) => {
    const url = getDroppedImportUrl(event.dataTransfer)
    if (url) {
      localImport.navigateToUrlImport(url)
      return
    }
    localImport.detectUpload(files)
  })

  onMounted(async () => {
    creator.reset()
    localImport.dndFile = null
    remoteImport.dndUrl = null

    const onboardingCompleted = await user.getExtraSettingByKey(user.userSettingLabels.ONBOARDING_COMPLETED)
    if (onboardingCompleted !== '1') {
      await modal.open<string>(() => import('@/components/modal/DesModalOnboarding.vue'))
    } else {
      const newVersionInfoShown = await user.getExtraSettingByKey(user.userSettingLabels.NEW_VERSION_INFO_SHOWN)
      const versionNumber = parseFloat(newVersionInfoShown || '0')
      const notNumber = isNaN(versionNumber)

      if (notNumber || versionNumber < 4.5) {
        await modal.open<string>(() => import('@/components/modal/DesModalNewVersion.vue'))
      }
      // temporarily commented for now
      // else if (notNumber || versionNumber < 4.8) {
      //   await modal.open(() => import('@/components/modal/DesModalLaunchPrintBooks.vue'))
      //   await modal.open(() => import('@/components/modal/DesModalNewVersionPrintbooks.vue'))
      // }
    }
  })
</script>

<template>
  <DesLayout gradient>
    <template #search>
      <DesSearchWithResults />
    </template>
    <template #menu></template>
    <template #main>
      <div class="flex flex-col gap-9 mb-48">
        <DesEnter>
          <div
            class="grid gap-6 lg:grid-cols-2"
            :class="{
              'xl:grid-cols-4': canUsePrintBooks && canUseAudiobooks,
              'xl:grid-cols-3': canUsePrintBooks !== canUseAudiobooks,
            }"
          >
            <DesTile
              @click="track('project-creating-type', { type: 'printbook' })"
              image="/dashboard/new-project/type-printbook.png"
              title="New Print Book"
              icon="fa-light fa-plus"
              desc="Standalone print book creation"
              to="/creator/printbook"
              v-if="canUsePrintBooks"
            />
            <DesTile
              @click="track('project-creating-type', { type: 'ebook' })"
              image="/dashboard/new-project/type-ebook.png"
              title="New eBook"
              icon="fa-light fa-plus"
              desc="Start writing on a blank page or import"
              to="/creator/ebook"
            />
            <DesTile
              @click="track('project-creating-type', { type: 'audiobook' })"
              image="/dashboard/new-project/type-audiobook.png"
              title="New Audiobook"
              icon="fa-light fa-plus"
              desc="Start writing on a blank page or import"
              to="/creator/audiobook"
              v-if="canUseAudiobooks"
            />
            <!--
            <DesTile
              @click="track('project-creating-type', { type: 'presentation' })"
              image="/dashboard/new-project/type-presentation.png"
              title="New Presentation"
              icon="fa-light fa-plus"
              desc="Start creating a presentation"
              to="/creator/presentation"
              v-if="canUsePresentation"
            />
            -->
            <DesTileDnD :dropped-files="droppedFiles" :url="droppedUrl" />
          </div>
        </DesEnter>

        <DesEnter :delay="250">
          <DesHomeHero />
        </DesEnter>

        <DesEnter :delay="500">
          <DesHomeChatWidget />
        </DesEnter>

        <DesEnter :delay="1000">
          <DesHomeRecentProjects />
        </DesEnter>
      </div>
    </template>
  </DesLayout>
</template>
